/**
 * Reusable email-OTP form. Two-step:
 *   1. Enter email   → POST /auth/request-code
 *   2. Enter 6-digit → POST /auth/verify-code  → set session, fire onSuccess
 *
 * Used by /login (large, centered) and the landing page (compact inline).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Auth } from '@/lib/api/v1';
import { Button, Input, Icon } from '@/design-system';
import { useSession, useToasts, usePrefs } from '@/lib/store';

interface EmailOtpFormProps {
  /** Compact = used inline on landing; large = full-screen login. */
  variant?: 'compact' | 'large';
  /** Where to navigate after a successful login. Defaults to /cellar (or onboarding if no household). */
  redirectTo?: string;
  /** Called after successful sign-in (parent can dismiss its modal/drawer). */
  onSuccess?: () => void;
}

export function EmailOtpForm({ variant = 'compact', redirectTo, onSuccess }: EmailOtpFormProps) {
  const navigate = useNavigate();
  const { setSession } = useSession();
  const { setActiveTenantId } = usePrefs();
  const { push } = useToasts();

  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [resendIn, setResendIn] = useState(0);
  // Inline error shown directly in the form. Toasts can be missed (auto-dismiss,
  // off-screen on mobile), and the user explicitly wants login failures visible.
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const requestCode = useMutation({
    mutationFn: () => Auth.requestCode(email),
    onSuccess: (data) => {
      // Only advance to the code step on a real send. An invite-only rejection
      // surfaces as onError, so the user stays on the email step with the reason.
      setError(null);
      setStep('code');
      setResendIn(data.cooldownSeconds ?? 60);
      setTimeout(() => codeRefs.current[0]?.focus(), 60);
    },
    onError: (err: Error) => {
      setError(err.message);
      push({ title: 'Kunne ikke sende kode', description: err.message, tone: 'danger' });
    },
  });

  const verifyCode = useMutation({
    mutationFn: () => Auth.verifyCode(email, code.join('')),
    onSuccess: (data) => {
      setError(null);
      setSession({ user: data.user, memberships: data.memberships });
      if (data.defaultTenantId) setActiveTenantId(data.defaultTenantId);
      push({ title: 'Velkommen', description: data.user.email, tone: 'success' });
      onSuccess?.();
      const dest = redirectTo ?? (data.memberships.length === 0 ? '/onboarding/create-household' : '/cellar');
      navigate(dest, { replace: true });
    },
    onError: (err: Error) => {
      setError(err.message);
      push({ title: 'Feil kode', description: err.message, tone: 'danger' });
    },
  });

  const setDigit = (idx: number, value: string) => {
    if (error) setError(null);
    const cleaned = value.replace(/\D/g, '').slice(0, 1);
    setCode((prev) => {
      const next = [...prev];
      next[idx] = cleaned;
      return next;
    });
    if (cleaned && idx < 5) codeRefs.current[idx + 1]?.focus();
    if (cleaned && idx === 5 && code.slice(0, 5).every(Boolean)) {
      setTimeout(() => verifyCode.mutate(), 80);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const arr = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) arr[i] = text[i] ?? '';
    setCode(arr);
    const last = Math.min(text.length - 1, 5);
    codeRefs.current[last]?.focus();
    if (arr.every(Boolean)) setTimeout(() => verifyCode.mutate(), 80);
  };

  const compact = variant === 'compact';
  // Mobile: keep all 6 digits inside the viewport (iPhone SE = 320px). Scales up on sm+.
  const inputSize = compact
    ? 'h-11 w-9 text-2xl'
    : 'h-14 w-9 !px-1 text-2xl sm:w-12 sm:!px-3 sm:text-3xl';
  const submitSize = compact ? 'md' : 'lg';

  if (step === 'email') {
    return (
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (email.length > 0) requestCode.mutate();
        }}
      >
        <Input
          iconLeft="mail"
          label={compact ? undefined : 'E-post'}
          type="email"
          autoComplete="email"
          placeholder="navn@domene.no"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError(null);
          }}
          required
          autoFocus={!compact}
        />
        {error && (
          <p className="rounded-md border border-danger/35 bg-danger/10 px-3 py-2 text-xs text-parchment">
            {error}
          </p>
        )}
        <Button
          type="submit"
          variant="primary"
          size={submitSize}
          fullWidth
          loading={requestCode.isPending}
          iconRight="arrow-right"
        >
          {compact ? 'Send kode' : 'Send kode på e-post'}
        </Button>
        {!compact && (
          <p className="text-center text-xs text-mist">
            Ingen passord. Bare en kode per innlogging — utløper på 10 minutter.
          </p>
        )}
      </form>
    );
  }

  return (
    <form
      className="grid gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.every(Boolean)) verifyCode.mutate();
      }}
    >
      <p className="text-xs text-mist">
        Vi sendte en 6-sifret kode til <span className="text-parchment">{email}</span>.
      </p>
      {error && (
        <p className="rounded-md border border-danger/35 bg-danger/10 px-3 py-2 text-xs text-parchment">
          {error}
        </p>
      )}
      <div className={`flex justify-between gap-1.5 ${compact ? '' : 'sm:gap-3'}`} onPaste={onPaste}>
        {code.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              codeRefs.current[i] = el;
            }}
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            value={d}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !d && i > 0) codeRefs.current[i - 1]?.focus();
            }}
            className={`ui-input text-center font-serif ${inputSize}`}
            aria-label={`siffer ${i + 1}`}
          />
        ))}
      </div>
      <Button
        type="submit"
        variant="primary"
        size={submitSize}
        fullWidth
        loading={verifyCode.isPending}
        iconRight="check"
        disabled={!code.every(Boolean)}
      >
        Bekreft
      </Button>
      <div className="flex items-center justify-between text-xs text-mist">
        <button
          type="button"
          className="hover:text-candle"
          onClick={() => {
            setStep('email');
            setCode(['', '', '', '', '', '']);
          }}
        >
          <Icon name="chevron-left" size={10} className="inline" /> Bytt e-post
        </button>
        <button
          type="button"
          disabled={resendIn > 0 || requestCode.isPending}
          onClick={() => requestCode.mutate()}
          className="hover:text-candle disabled:opacity-50"
        >
          {resendIn > 0 ? `Send på nytt om ${resendIn}s` : 'Send ny kode'}
        </button>
      </div>
    </form>
  );
}
